import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Box, Container, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import { ApolloError } from 'common/components/ApolloError';
import { Link } from 'common/components/Link';
import LIBRARY_EMPTY_IMAGE from 'assets/images/library.webp';
import { BasePage } from 'hydra/pages/BasePage';
import { Button } from 'common/components/Button';
import { Img } from 'common/components/Img';
import { IFeature, CreateCollectionDocument } from 'graphql/types';
import { PageTitle } from 'common/components/PageTitle';
import { UpgradeContent } from 'library/components/UpgradeContent';
import { useFeature } from 'common/hooks/useFeature';

interface IProps {
    onCreate?(): void;
    upgradeRequired?: boolean;
}

export const LibraryEmptyPage = ({ onCreate, upgradeRequired }: IProps) => {
    const [translate] = useTranslation();
    const [isCreating, setIsCreating] = useState(false);
    const { userCanSeeUpgrade } = useFeature(IFeature.Library);

    const [createCollection, { error: createError }] = useMutation(
        CreateCollectionDocument
    );

    const showUpgradePage = upgradeRequired && userCanSeeUpgrade;

    return (
        <BasePage contentContainerMaxWidth={showUpgradePage ? 'lg' : undefined}>
            {createError && <ApolloError error={createError} />}
            {!upgradeRequired && (
                <Container maxWidth="sm">
                    <Box sx={{ mt: 8, textAlign: 'center' }}>
                        <Box
                            sx={{
                                maxWidth: 400,
                                mb: 4,
                                mx: 'auto',
                            }}
                        >
                            <Img src={LIBRARY_EMPTY_IMAGE} />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h1">
                                {translate('library')}
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 4 }}>
                            <Typography>
                                <Trans
                                    components={[
                                        <Link
                                            href="http://helpdesk.pluvo.nl/"
                                            key="0"
                                            target="_blank"
                                        />,
                                    ]}
                                    i18nKey="libraryEmptyPage.text"
                                />
                            </Typography>
                        </Box>

                        <>
                            <PageTitle>
                                {translate('libraryEmptyPage.title')}
                            </PageTitle>
                            <Button
                                color="primary"
                                disabled={isCreating}
                                variant="contained"
                                onClick={async () => {
                                    setIsCreating(true);
                                    // Create the root collection with some default configuration
                                    const newCollection = {
                                        title: translate('library'),
                                        root: true,
                                    };

                                    try {
                                        await createCollection({
                                            variables: {
                                                collection: newCollection,
                                            },
                                        });
                                        onCreate && onCreate();
                                    } catch {
                                        // Error handled by hook
                                    }
                                }}
                            >
                                {translate('libraryEmptyPage.buttonText')}
                            </Button>
                        </>
                    </Box>
                </Container>
            )}
            {showUpgradePage && <UpgradeContent />}
        </BasePage>
    );
};
